import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { register, createCheckout, updateCompany } from '../api/client'
import { ChefHat, ArrowRight, Zap, X } from 'lucide-react'
import toast from 'react-hot-toast'

const PLAN_LABELS: Record<string, string> = {
  starter_monthly: 'Alap csomag — havi (2 990 Ft/hó + ÁFA)',
  starter_annual:  'Alap csomag — éves (29 900 Ft/év + ÁFA)',
  pro_monthly:     'Pro csomag — havi (5 990 Ft/hó + ÁFA)',
  pro_annual:      'Pro csomag — éves (59 900 Ft/év + ÁFA)',
}

interface BillingForm {
  name: string
  tax_number: string
  address: string
  email: string
}

function BillingModal({
  onConfirm,
  onClose,
  loading,
  defaultEmail,
}: {
  onConfirm: (data: BillingForm) => void
  onClose: () => void
  loading: boolean
  defaultEmail: string
}) {
  const [form, setForm] = useState<BillingForm>({ name: '', tax_number: '', address: '', email: defaultEmail })
  const needsTax = form.name.trim().length > 0

  function set(k: keyof BillingForm) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (needsTax && !form.tax_number.trim()) {
      toast.error('Cégnév megadása esetén az adószám kötelező')
      return
    }
    onConfirm(form)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X size={20} />
        </button>

        <h2 className="text-lg font-bold mb-1" style={{ color: '#1E2D27' }}>Számlázási adatok</h2>
        <p className="text-sm text-gray-500 mb-5">
          Ezek az adatok kerülnek a számlára. Utólag a Beállításokban is módosíthatók.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Cégnév / vállalkozás neve</label>
            <input className="input" placeholder="pl. Napsugár Cukrászda Kft." value={form.name} onChange={set('name')} />
          </div>

          <div>
            <label className="label">
              Adószám
              {needsTax && <span className="text-red-500 ml-1">*</span>}
              {!needsTax && <span className="text-gray-400 font-normal text-xs ml-1">— opcionális magánszemélynek</span>}
            </label>
            <input
              className="input"
              placeholder="12345678-1-23"
              value={form.tax_number}
              onChange={set('tax_number')}
              required={needsTax}
            />
          </div>

          <div>
            <label className="label">Számlázási cím</label>
            <input className="input" placeholder="1111 Budapest, Példa u. 1." value={form.address} onChange={set('address')} />
          </div>

          <div>
            <label className="label">E-mail (számla értesítőhöz)</label>
            <input className="input" type="email" placeholder="nev@email.hu" value={form.email} onChange={set('email')} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 mt-2"
            style={{ background: '#5BB8A0', color: '#fff' }}
          >
            {loading ? 'Átirányítás a fizetéshez...' : <><Zap className="w-4 h-4" /> Tovább a fizetéshez</>}
          </button>
        </form>
      </div>
    </div>
  )
}

export default function RegisterPage() {
  const [form, setForm] = useState({ company_name: '', name: '', email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [showBilling, setShowBilling] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const ref = searchParams.get('ref') || undefined
  const plan = searchParams.get('plan') || undefined

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.company_name.trim() || !form.name.trim() || !form.email.trim() || !form.password) {
      toast.error('Kérlek tölts ki minden mezőt')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Adj meg egy érvényes e-mail címet')
      return
    }
    if (form.password.length < 8) {
      toast.error('A jelszónak legalább 8 karakter hosszúnak kell lennie')
      return
    }
    setLoading(true)
    try {
      const params = ref ? `?ref=${encodeURIComponent(ref)}` : ''
      const res = await register(form, params)
      localStorage.setItem('token', res.data.access_token)
      if (plan && PLAN_LABELS[plan]) {
        setShowBilling(true)
        setLoading(false)
      } else {
        toast.success('Sikeres regisztráció!')
        navigate('/receptek')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Hiba a regisztráció során')
      setLoading(false)
    }
  }

  async function handleBillingConfirm(data: BillingForm) {
    if (!plan) return
    setLoading(true)
    try {
      await updateCompany(data)
      const checkout = await createCheckout(plan)
      window.location.href = checkout.data.checkout_url
    } catch {
      toast.error('Hiba a fizetési felület megnyitásakor')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#F0FAF7' }}>
      {showBilling && plan && (
        <BillingModal
          onConfirm={handleBillingConfirm}
          onClose={() => { setShowBilling(false); navigate('/receptek') }}
          loading={loading}
          defaultEmail={form.email}
        />
      )}

      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4 hover:opacity-80 transition-opacity"
               style={{ background: '#1E2D27' }}>
            <ChefHat className="w-6 h-6" style={{ color: '#5BB8A0' }} />
          </Link>
          <h1 className="text-2xl font-bold" style={{ color: '#1E2D27' }}>Regisztráció</h1>
          {ref && /^CD\d+$/i.test(ref) && (
            <p className="mt-2 text-xs font-semibold px-3 py-1.5 rounded-full inline-block"
              style={{ background: 'rgba(91,184,160,0.15)', color: '#3f8f7a' }}>
              🎁 Ajánlással érkeztél — előfizetés után egy hónap ajándék
            </p>
          )}
          {plan && PLAN_LABELS[plan] ? (
            <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(91,184,160,0.15)', color: '#5BB8A0', border: '1px solid rgba(91,184,160,0.3)' }}>
              <Zap size={11} />
              {PLAN_LABELS[plan]}
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-1">Hozz létre ingyenes fiókot</p>
          )}
          {plan && PLAN_LABELS[plan] && (
            <p className="text-xs text-gray-500 mt-2">
              Az előfizetés {plan.endsWith('_annual') ? 'évente' : 'havonta'} automatikusan megújul, amíg le nem mondod.
              Bármikor lemondható az Előfizetés oldalon.
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-7">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label className="label">Cukrászda / cég neve</label>
              <input className="input" placeholder="pl. Napsugár Pékség és Cukrászda"
                value={form.company_name} onChange={set('company_name')} />
            </div>
            <div>
              <label className="label">Az ön neve</label>
              <input className="input" placeholder="Tóth Katalin"
                value={form.name} onChange={set('name')} />
            </div>
            <div>
              <label className="label">E-mail cím</label>
              <input className="input" type="email" placeholder="nev@email.hu"
                value={form.email} onChange={set('email')} />
            </div>
            <div>
              <label className="label">Jelszó <span className="text-gray-400 font-normal normal-case tracking-normal">(min. 8 karakter)</span></label>
              <input className="input" type="password" placeholder="••••••••"
                value={form.password} onChange={set('password')} />
            </div>
            <button type="submit" className="btn-primary w-full mt-2" disabled={loading}>
              {loading ? 'Regisztráció...' : (
                plan && PLAN_LABELS[plan]
                  ? <><Zap className="w-4 h-4" /> Regisztráció</>
                  : <>Regisztráció és belépés <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>
        </div>

        <p className="text-sm text-center text-gray-400 mt-5">
          Már van fiókod?{' '}
          <Link to="/login" className="font-semibold hover:underline" style={{ color: '#5BB8A0' }}>
            Bejelentkezés
          </Link>
        </p>
      </div>
    </div>
  )
}
